/**
 *  EVERY localStorage KEY THE SAVE SYSTEM USES, IN A MODULE THAT IMPORTS
 *  NOTHING.
 *
 *  Two reasons it is its own file, and the second one is the important one.
 *
 *  ── 1. THEY WERE DUPLICATED ──────────────────────────────────────────────
 *  `STORAGE_KEY` lived in persist.ts, App.tsx declared its own `PERSIST_KEY`
 *  with the same string and a comment saying "must match", and
 *  `clearPersistedState` wrote two more as bare literals. Four copies of three
 *  keys. This project has been bitten repeatedly by one rule with several
 *  implementations; a storage key is the cheapest possible version of that
 *  mistake and the easiest to prevent.
 *
 *  ── 2. IMPORTING A CONSTANT MUST NOT BUILD THE REDUX STORE ───────────────
 *  Found by `.verify/check_boot_order.ts` on 2026-10-16, and it would have
 *  silently voided the entire cross-device fix:
 *
 *      saveTransport -> store/persist -> services/game/classGear
 *                    -> services/game/rarityColor -> store/store
 *
 *  `store/store` calls `loadPersistedState()` at module scope and builds the
 *  Redux state from localStorage once, for the life of the page. So merely
 *  importing `STORAGE_KEY` from persist.ts made the store evaluate during
 *  boot — from the OLD save — before the boot sequence had written the
 *  authoritative one. The fix was meant to remove the need to reload the page;
 *  without this split it would have quietly still needed it, and the phone
 *  would still have been stuck on level 95.
 *
 *  So: no imports in this file, ever. It is a leaf on purpose.
 */

/** The save blob itself. Must stay in sync with nothing — this is the source. */
export const STORAGE_KEY = 'elderglen-save-v1'

/** Which account the local save belongs to. Lets an account SWITCH be told
 *  apart from a sync, where picking the newer save would be meaningless
 *  because the two saves are different people. */
export const SAVE_OWNER_KEY = 'elderglen-save-owner'

/** The cloud revision (`saves.rev`, migration 0136) the local blob is known to
 *  match or descend from. Device bookkeeping, deliberately NOT a field inside
 *  the blob: `savePersistedState` rebuilds the blob from `{...state,
 *  _schemaVersion}` on every autosave, so anything else stored in there
 *  survives exactly until the first save and then vanishes. */
export const SYNCED_REV_KEY = 'elderglen-synced-rev'

/** Dead since boot stopped reloading the page to apply a cloud save, but old
 *  devices still carry one, and a reset should clear it. Kept here so the
 *  removal list has a name rather than a literal. */
export const LEGACY_HYDRATE_RELOADED_KEY = 'elderglen-hydrate-reloaded'
